/** Estatus en los que el ciudadano puede editar la solicitud. */
export const EDITABLE_STATUSES = ['Borrador', 'Responder observaciones'];

/** Catálogo unificado de estatus para bandeja y detalle admin. Debe coincidir con ProcedureRequest.Status en BD. */
export const ADMIN_REQUEST_STATUSES = [
  'Borrador',
  'Enviado',
  'En revisión',
  'Responder observaciones',
  'En proceso',
  'Aprobado',
  'Rechazado',
  'Entregado',
] as const;

export type AdminRequestStatus = typeof ADMIN_REQUEST_STATUSES[number];

/** Estatus de solo consulta para el ciudadano. */
export const READONLY_STATUSES = [
  'Enviado', 'En revisión', 'En proceso', 'Rechazado', 'Aprobado', 'Entregado',
];

/** Clase Bootstrap para badge de estatus en UI admin. */
export function adminStatusBadgeClass(status: string): string {
  const map: Record<string, string> = {
    'Borrador':                'badge-secondary',
    'Enviado':                 'badge-info',
    'En revisión':             'badge-warning',
    'Responder observaciones': 'badge-warning',
    'En proceso':              'badge-primary',
    'Aprobado':                'badge-success',
    'Rechazado':               'badge-danger',
    'Entregado':               'badge-dark',
  };
  return map[(status || '').trim()] || 'badge-light';
}

export function isAdminRequestStatus(status: string): boolean {
  return ADMIN_REQUEST_STATUSES.includes((status || '').trim() as AdminRequestStatus);
}

export function canCitizenEdit(status: string): boolean {
  return EDITABLE_STATUSES.includes((status || '').trim());
}

export function isReadOnlyStatus(status: string): boolean {
  return !canCitizenEdit(status);
}

/** Etiqueta amigable para la UI ciudadana. */
export function citizenDisplayStatus(status: string): string {
  const s = (status || '').trim();
  if (['Enviado', 'En proceso'].includes(s)) return 'En revisión';
  if (s === 'Rechazado') return 'Rechazada';
  if (s === 'Aprobado' || s === 'Entregado') return 'Aceptada';
  return s;
}

export function statusCssClass(status: string): string {
  const display = citizenDisplayStatus(status).toLowerCase();
  const map: Record<string, string> = {
    'borrador':               'borrador',
    'en revisión':            'en-revision',
    'responder observaciones':'observaciones',
    'rechazada':              'rechazado',
    'aceptada':               'aceptado',
  };
  return map[display] || 'pendiente';
}

export type RequestActionType = 'continue' | 'view' | 'respond' | 'download';

export interface RequestAction {
  type: RequestActionType;
  label: string;
}

export function getRequestAction(status: string): RequestAction | null {
  const s = (status || '').trim();
  if (s === 'Borrador') {
    return { type: 'continue', label: 'Continuar' };
  }
  if (['Enviado', 'En revisión', 'En proceso'].includes(s)) {
    return { type: 'view', label: 'Ver detalle' };
  }
  if (s === 'Responder observaciones') {
    return { type: 'respond', label: 'Responder' };
  }
  if (s === 'Rechazado') {
    return { type: 'view', label: 'Ver detalle' };
  }
  if (s === 'Aprobado' || s === 'Entregado') {
    return { type: 'view', label: 'Ver detalle' };
  }
  return null;
}

export function canDownloadOutput(status: string, outputDocumentId?: number | null): boolean {
  return (status === 'Aprobado' || status === 'Entregado') && !!outputDocumentId;
}
